import React, { createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createContextualCan } from '@casl/react';
import { defineAbilityFor, createEmptyAbility } from '../services/ability';
import api from '../services/api';
import { marcarPerfilContador } from '../utils/perfilContador';

// Contexto de permissoes
const PermissionsContext = createContext();

// Contexto de habilidades do CASL
const AbilityContext = createContext();

/**
 * Provider de permissoes que gerencia as abilities do usuario
 * Suporta escopo de loja para multi-tenant
 */
export function PermissionsProvider({ children }) {
  const [ability, setAbility] = useState(createEmptyAbility());
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estado para multi-loja
  const [currentLoja, setCurrentLoja] = useState(localStorage.getItem('sessionloja') || null);
  const [lojasDisponiveis, setLojasDisponiveis] = useState([]);
  const [isGerente, setIsGerente] = useState(false);
  const [tipoUsuario, setTipoUsuario] = useState(localStorage.getItem('sessionTipoUsuario') || 'usuario');

  /**
   * Carrega as permissoes do usuario da API
   * Considera o escopo da loja atual para multi-tenant
   */
  const loadPermissions = useCallback(async () => {
    const isLogged = JSON.parse(localStorage.getItem('isLogged') || 'false');
    const token = localStorage.getItem('sessionToken');
    const lojaAtual = localStorage.getItem('sessionloja');

    if (!isLogged || !token) {
      setAbility(createEmptyAbility());
      setPermissions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Carregar permissoes e lojas disponiveis em paralelo
      const [permissoesResp, lojasResp] = await Promise.all([
        api.get('/api/minhas-permissoes'),
        api.get('/v1/usuarios/minhas-lojas').catch(() => ({ data: [] }))
      ]);

      const userPermissions = permissoesResp.data || [];
      const lojas = lojasResp.data || [];

      setPermissions(userPermissions);
      marcarPerfilContador(userPermissions);
      setLojasDisponiveis(lojas);

      // Atualizar estado da loja atual
      setCurrentLoja(lojaAtual);
      setTipoUsuario(localStorage.getItem('sessionTipoUsuario') || 'usuario');

      // Verificar se e gerente da loja atual
      const lojaAtualInfo = lojas.find(l => l.idloja?.toString() === lojaAtual);
      setIsGerente(lojaAtualInfo?.is_gerente === 1);

      // Criar abilities com as permissoes
      const newAbility = defineAbilityFor(userPermissions);
      setAbility(newAbility);

      // Salvar lojas no localStorage para cache
      if (lojas.length > 0) {
        localStorage.setItem('sessionLojasDisponiveis', JSON.stringify(lojas));
      }

    } catch (err) {
      // Nao fazer logout aqui - o interceptor do api.js ja cuida disso centralmente
      // Apenas logar o erro e continuar com permissoes vazias
      const status = err.response?.status;
      if (status === 401) {
        console.warn('[Permissions] Erro 401 ao carregar permissoes. O interceptor do api.js cuidara do logout.');
      } else if (status === 403) {
        console.warn('[Permissions] Acesso negado (403) ao carregar permissoes. Continuando com permissoes vazias.');
      } else {
        console.warn('[Permissions] Erro ao carregar permissoes:', err.message);
      }

      setError(err.message || 'Erro ao carregar permissoes');
      setAbility(createEmptyAbility());
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Define permissoes diretamente a partir dos dados (evita chamada API)
   * Usado quando as permissoes ja vem no retorno do login
   */
  const setPermissionsFromData = useCallback((userPermissions, lojas = []) => {
    const lojaAtual = localStorage.getItem('sessionloja');

    setPermissions(userPermissions || []);
    marcarPerfilContador(userPermissions || []);
    setLojasDisponiveis(lojas);

    // Atualizar estado da loja atual
    setCurrentLoja(lojaAtual);
    setTipoUsuario(localStorage.getItem('sessionTipoUsuario') || 'usuario');

    // Verificar se e gerente da loja atual
    if (lojas.length > 0) {
      const lojaAtualInfo = lojas.find(l => l.idloja?.toString() === lojaAtual);
      setIsGerente(lojaAtualInfo?.is_gerente === 1);
    }

    // Criar abilities com as permissoes
    const newAbility = defineAbilityFor(userPermissions || []);
    setAbility(newAbility);

    setLoading(false);
    setError(null);
  }, []);

  /**
   * Limpa as permissoes (usado no logout)
   */
  const clearPermissions = useCallback(() => {
    setAbility(createEmptyAbility());
    setPermissions([]);
    setLojasDisponiveis([]);
    setCurrentLoja(null);
    setIsGerente(false);
    setTipoUsuario('usuario');
    setError(null);
  }, []);

  /**
   * Atualiza a loja atual e recarrega permissoes se necessario
   */
  const trocarLoja = useCallback(async (idloja) => {
    setCurrentLoja(idloja);
    localStorage.setItem('sessionloja', idloja);

    // Verificar se e gerente da nova loja
    const lojaInfo = lojasDisponiveis.find(l => l.idloja?.toString() === idloja?.toString());
    setIsGerente(lojaInfo?.is_gerente === 1);

    // Recarregar permissoes pode ser necessario se houver permissoes por loja
    // await loadPermissions();
  }, [lojasDisponiveis]);

  /**
   * Verifica se o usuario tem uma permissao especifica
   */
  const can = useCallback((action, subject) => {
    return ability.can(action, subject);
  }, [ability]);

  /**
   * Verifica se o usuario NAO tem uma permissao especifica
   */
  const cannot = useCallback((action, subject) => {
    return ability.cannot(action, subject);
  }, [ability]);

  /**
   * Verifica se o usuario tem pelo menos uma das permissoes listadas
   */
  const canAny = useCallback((permissionsToCheck) => {
    return permissionsToCheck.some(({ action, subject }) => ability.can(action, subject));
  }, [ability]);

  /**
   * Verifica se o usuario tem todas as permissoes listadas
   */
  const canAll = useCallback((permissionsToCheck) => {
    return permissionsToCheck.every(({ action, subject }) => ability.can(action, subject));
  }, [ability]);

  /**
   * Verifica se o usuario e Super Admin (admin do sistema)
   */
  const isSuperAdmin = useCallback(() => {
    return tipoUsuario === 'super_admin';
  }, [tipoUsuario]);

  /**
   * Verifica se o usuario e Admin do Cliente
   */
  const isAdminCliente = useCallback(() => {
    return tipoUsuario === 'admin_cliente';
  }, [tipoUsuario]);

  /**
   * Verifica se o usuario e Gerente da loja atual
   */
  const isGerenteLoja = useCallback(() => {
    return isGerente;
  }, [isGerente]);

  /**
   * Verifica se o usuario tem acesso a uma loja especifica
   */
  const temAcessoLoja = useCallback((idloja) => {
    return lojasDisponiveis.some(l => l.idloja?.toString() === idloja?.toString());
  }, [lojasDisponiveis]);

  const value = {
    ability,
    permissions,
    loading,
    error,
    loadPermissions,
    setPermissionsFromData,
    clearPermissions,
    can,
    cannot,
    canAny,
    canAll,
    // Multi-loja
    currentLoja,
    lojasDisponiveis,
    isGerente,
    tipoUsuario,
    trocarLoja,
    isSuperAdmin,
    isAdminCliente,
    isGerenteLoja,
    temAcessoLoja
  };

  return (
    <PermissionsContext.Provider value={value}>
      <AbilityContext.Provider value={ability}>
        {children}
      </AbilityContext.Provider>
    </PermissionsContext.Provider>
  );
}

PermissionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook para acessar o contexto de permissoes
 */
export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions deve ser usado dentro de um PermissionsProvider');
  }
  return context;
}

/**
 * Hook para acessar a ability do CASL diretamente
 */
export function useAbility() {
  const context = useContext(AbilityContext);
  if (context === undefined) {
    throw new Error('useAbility deve ser usado dentro de um PermissionsProvider');
  }
  return context;
}

/**
 * Componente Can do CASL contextualizado
 * Uso: <Can I="create" a="produtos">...</Can>
 */
export const Can = createContextualCan(AbilityContext.Consumer);

export default PermissionsContext;
